import { useState, useMemo } from 'react';
import { Calendar, Clock, User, MapPin, X, AlertCircle } from 'lucide-react';
import { usePortalAppointments, usePortalCancelAppointment } from '@/hooks/usePortalAppointments';
import type { PortalAppointmentItem } from '@/hooks/usePortalAppointments';

/**
 * Patient portal appointments page.
 * Shows upcoming and past appointments with cancellation capability.
 */
export function PortalAppointments() {
  const { data: appointments = [], isLoading, error } = usePortalAppointments();
  const [activeTab, setActiveTab] = useState<'upcoming' | 'past'>('upcoming');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<PortalAppointmentItem | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const cancelMutation = usePortalCancelAppointment();

  // Split appointments into upcoming and past
  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const upcomingList: PortalAppointmentItem[] = [];
    const pastList: PortalAppointmentItem[] = [];

    appointments.forEach((apt) => {
      const startsAt = new Date(apt.starts_at);
      if (startsAt >= now && apt.status !== 'cancelled') {
        upcomingList.push(apt);
      } else {
        pastList.push(apt);
      }
    });

    // Sort upcoming ascending (soonest first)
    upcomingList.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
    // Past appointments are already sorted descending from the query

    return { upcoming: upcomingList, past: pastList };
  }, [appointments]);

  const handleCancelClick = (appointment: PortalAppointmentItem) => {
    setSelectedAppointment(appointment);
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointment) return;

    try {
      await cancelMutation.mutateAsync({
        appointment_id: selectedAppointment.id,
        reason: cancelReason || 'Cancelled by patient via portal',
      });
      setShowCancelModal(false);
      setSelectedAppointment(null);
      setCancelReason('');
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
    }
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setSelectedAppointment(null);
    setCancelReason('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
        <div className="flex items-center text-red-800 dark:text-red-200">
          <AlertCircle className="mr-2 h-5 w-5" />
          <span>Failed to load appointments. Please try again.</span>
        </div>
      </div>
    );
  }

  const displayedAppointments = activeTab === 'upcoming' ? upcoming : past;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Appointments</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          View and manage your upcoming and past appointments
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'upcoming'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            }`}
          >
            Upcoming ({upcoming.length})
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
              activeTab === 'past'
                ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
            }`}
          >
            Past ({past.length})
          </button>
        </nav>
      </div>

      {/* Appointments List */}
      {displayedAppointments.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            No {activeTab} appointments
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {activeTab === 'upcoming'
              ? 'You have no upcoming appointments scheduled.'
              : 'You have no past appointments.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {displayedAppointments.map((appointment) => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              onCancel={handleCancelClick}
            />
          ))}
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Cancel Appointment
              </h3>
              <button
                onClick={handleCloseCancelModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to cancel your appointment on{' '}
                {formatDate(selectedAppointment.starts_at)} at{' '}
                {formatTime(selectedAppointment.starts_at)}?
              </p>

              <div className="mt-4">
                <label
                  htmlFor="cancel-reason"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Reason (optional)
                </label>
                <textarea
                  id="cancel-reason"
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  placeholder="Optional cancellation reason..."
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleCloseCancelModal}
                disabled={cancelMutation.isPending}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Keep Appointment
              </button>
              <button
                onClick={handleCancelConfirm}
                disabled={cancelMutation.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Appointment'}
              </button>
            </div>

            {cancelMutation.isError && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-800 dark:text-red-200">
                  Failed to cancel appointment. Please try again or contact the clinic.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface AppointmentCardProps {
  appointment: PortalAppointmentItem;
  onCancel: (appointment: PortalAppointmentItem) => void;
}

function AppointmentCard({ appointment, onCancel }: AppointmentCardProps) {
  const statusColors = {
    upcoming: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    completed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    no_show: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {/* Clinic Info */}
          <div className="flex items-center space-x-3">
            {appointment.clinic_logo ? (
              <img
                src={appointment.clinic_logo}
                alt={appointment.clinic_name}
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
                <MapPin className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {appointment.clinic_name}
              </h3>
              {appointment.service_name && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {appointment.service_name}
                </p>
              )}
            </div>
          </div>

          {/* Appointment Details */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Calendar className="mr-2 h-4 w-4" />
              <span>{formatDate(appointment.starts_at)}</span>
            </div>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <Clock className="mr-2 h-4 w-4" />
              <span>
                {formatTime(appointment.starts_at)} - {formatTime(appointment.ends_at)}
              </span>
            </div>
            {appointment.clinician_name && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <User className="mr-2 h-4 w-4" />
                <span>{appointment.clinician_name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Status Badge */}
        <div className="ml-4">
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
              statusColors[appointment.status] ?? statusColors.upcoming
            }`}
          >
            {appointment.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </div>

      {/* Actions */}
      {appointment.cancellation_allowed && (
        <div className="mt-4 flex justify-end border-t border-gray-200 pt-4 dark:border-gray-700">
          <button
            onClick={() => onCancel(appointment)}
            className="text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            Cancel Appointment
          </button>
        </div>
      )}
    </div>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}
